import { CloseRounded } from '@mui/icons-material';
import {
  type ActionProps,
  type OperatorSelectorProps,
  type RuleGroupType,
  type RuleProps,
  type RuleType,
  useRule,
  type ValidationResult,
  type ValueEditorProps
} from 'react-querybuilder';
import {
  type AudienceOperator,
  audienceField,
  dependsOnHint,
  isNegatableField,
  lookbackOptionsFor,
  NEGATED_OPERATOR,
  OPERATOR_LABELS,
  rangeBounds,
  ruleFlatKeys,
  ruleLabel,
  STATE_OPTIONS,
  toNumber
} from '../../lib/audienceFields';
import { isPurchaseLookbackWindow } from '../../lib/filters';
import type { FilterErrors } from '../../lib/filterValidation';
import {
  ANY_GENDER,
  addOppositeRule,
  isValueReason,
  oppositeOperator,
  slotTaken,
  toggleRuleSide
} from '../../lib/queryMapping';
import { VisuallyHidden } from '../shared.styles';
import { BrandMultiSelect } from './BrandMultiSelect';
import {
  ChipEntry,
  MultiSelect,
  NumberField,
  RangeField,
  SegmentedControl
} from './FilterControls';
import {
  AddSideButton,
  FieldError,
  FieldHint,
  NotAppliedNote,
  NotMarker,
  NotToggle,
  RemoveRuleButton,
  RuleEditors,
  RuleInline,
  RuleLabel,
  RuleLabelLine,
  RuleRowBox
} from './FilterRules.styles';
import SupportBadge from './SupportBadge';

/**
 * Passed as the QueryBuilder `context`.
 *
 * - `filterErrors` are cross-field errors the per-rule validators cannot see.
 * - `droppedReasons` maps a rule id to why that rule is left out of the filters.
 */
export type FilterRulesContext = {
  filterErrors?: FilterErrors;
  droppedReasons?: Record<string, string>;
};

/** One editor per field, picked from the audience field config. */
export const AudienceValueEditor = (props: ValueEditorProps) => {
  const field = audienceField(props.field);
  const { disabled, value, handleOnChange } = props;
  const invalid = reasonsOf(props.validation).length > 0;
  if (!field) return null;

  switch (field.editor) {
    case 'gender':
      return (
        <SegmentedControl
          label={field.label}
          value={value}
          disabled={disabled}
          options={GENDER_OPTIONS}
          onChange={handleOnChange}
        />
      );
    case 'tristate': {
      const [yes, no] = field.choiceLabels;
      return (
        <SegmentedControl
          label={field.label}
          value={value}
          disabled={disabled}
          options={[
            { label: yes, value: true },
            { label: no, value: false }
          ]}
          onChange={handleOnChange}
        />
      );
    }
    case 'choice':
      return (
        <SegmentedControl
          label={field.label}
          value={value}
          disabled={disabled}
          options={field.options.map(option => ({
            label: option.label,
            value: option.value
          }))}
          onChange={handleOnChange}
        />
      );
    case 'lookback': {
      const current = toNumber(value) ?? null;
      return (
        <SegmentedControl
          label={field.label}
          value={current}
          disabled={disabled}
          options={[
            /* A legacy window outside PURCHASE_LOOKBACK_WINDOWS shows as saved but cannot be picked */
            ...lookbackOptionsFor(current).map(days => ({
              label: `${days} days`,
              value: days,
              disabled: !isPurchaseLookbackWindow(days)
            }))
          ]}
          onChange={handleOnChange}
        />
      );
    }
    case 'range':
    case 'percentRange': {
      const percent = field.editor === 'percentRange';
      const read = (bound: unknown) => (percent ? toPercent(bound) : (toNumber(bound) ?? null));
      const write = (next: number | null) => (percent ? toFraction(next) : emptyToBlank(next));
      if (props.operator === 'between') {
        const [low, high] = rangeBounds(value).map(read);
        return (
          <RangeField
            label={field.label}
            min={low}
            max={high}
            prefix={field.prefix}
            invalid={invalid}
            disabled={disabled}
            onChange={(min, max) => handleOnChange([write(min), write(max)])}
          />
        );
      }
      return (
        <NumberField
          label={field.label}
          value={read(value)}
          prefix={field.prefix}
          invalid={invalid}
          disabled={disabled}
          onChange={next => handleOnChange(write(next))}
        />
      );
    }
    case 'number':
      return (
        <NumberField
          label={field.label}
          value={toNumber(value) ?? null}
          placeholder="e.g. 100,000"
          invalid={invalid}
          disabled={disabled}
          onChange={next => handleOnChange(emptyToBlank(next))}
        />
      );
    case 'states':
      return (
        <MultiSelect
          label={ruleLabel(field, props.operator)}
          tone={sideTone(props.operator)}
          values={asList(value)}
          options={STATE_OPTIONS}
          unavailable={otherSideValues(props)}
          invalid={invalid}
          disabled={disabled}
          placeholder="Search states…"
          renderChip={option => option.value}
          onChange={handleOnChange}
        />
      );
    case 'options':
      return (
        <MultiSelect
          label={field.label}
          tone={field.tone}
          values={asList(value)}
          options={field.options}
          invalid={invalid}
          disabled={disabled}
          placeholder={field.placeholder}
          onChange={handleOnChange}
        />
      );
    case 'chips':
      return (
        <ChipEntry
          label={field.label}
          tone={field.tone}
          values={asList(value)}
          invalid={invalid}
          disabled={disabled}
          placeholder={field.placeholder}
          onChange={handleOnChange}
        />
      );
    case 'brandIds':
      return (
        <BrandMultiSelect
          label={ruleLabel(field, props.operator)}
          tone={sideTone(props.operator)}
          values={asList(value)}
          unavailable={otherSideValues(props)}
          invalid={invalid}
          disabled={disabled}
          placeholder="Search brands by name…"
          onChange={handleOnChange}
        />
      );
    default:
      return null;
  }
};

/**
 * One vertical row per rule: label and hint on the left, operator and value in the middle, remove on the right.
 * The field is fixed per row; the column mapper adds and removes rows.
 *
 * A negatable field's row also carries:
 * - a Not switch by the label, which moves the rule between the include and exclude side;
 * - an add button for the other side while that side has no rule.
 *
 * A read-only row shows neither; an exclude rule shows a static Not marker in the switch's place.
 */
export const AudienceRuleRow = (props: RuleProps) => {
  const rule = useRule(props);
  const field = audienceField(rule.rule.field);
  const {
    operatorSelector: OperatorSelector,
    valueEditor: ValueEditor,
    removeRuleAction: RemoveRule
  } = rule.schema.controls;
  const context = (rule.context ?? {}) as FilterRulesContext;
  const crossFieldErrors = (field ? ruleFlatKeys(field, rule.rule.operator) : [])
    .map(key => context.filterErrors?.[key])
    .filter((message): message is string => Boolean(message));
  const errors = [...reasonsOf(rule.validationResult), ...crossFieldErrors];
  const dropped = rule.rule.id ? context.droppedReasons?.[rule.rule.id] : undefined;
  /* The validation error already names a missing or malformed value, so its Not applied note is redundant */
  const droppedReason =
    dropped && !(errors.length > 0 && isValueReason(dropped)) ? dropped : undefined;
  const common = {
    path: rule.path,
    level: rule.path.length,
    schema: rule.schema,
    context: rule.context,
    disabled: rule.disabled,
    validation: rule.validationResult
  };
  const label = field ? ruleLabel(field, rule.rule.operator) : rule.rule.field;
  const query = rule.schema.getQuery() as RuleGroupType | undefined;
  const negatable = isNegatableField(field) ? field : null;
  const negated = rule.rule.operator === NEGATED_OPERATOR;
  const otherSideTaken =
    negatable && query
      ? slotTaken(query, negatable, oppositeOperator(rule.rule.operator), rule.rule.id)
      : false;
  const otherSideLabel = negatable
    ? ruleLabel(negatable, oppositeOperator(rule.rule.operator))
    : '';
  const editQuery = (edit: (current: RuleGroupType, ruleId: string) => RuleGroupType) => {
    const current = rule.schema.getQuery() as RuleGroupType | undefined;
    if (current && rule.rule.id) rule.schema.dispatchQuery(edit(current, rule.rule.id));
  };
  const lookbackRule = rule.schema
    .getQuery()
    ?.rules.find(
      item => typeof item !== 'string' && 'field' in item && item.field === 'purchaseLookbackDays'
    ) as RuleType | undefined;
  const dependency = dependsOnHint(
    field,
    lookbackRule ? (toNumber(lookbackRule.value) ?? null) : null
  );
  const notReasonId = `not-reason-${rule.rule.id ?? rule.path.join('-')}`;

  return (
    <RuleRowBox invalid={errors.length > 0 && !rule.disabled} data-rule-field={rule.rule.field}>
      <RuleLabel>
        <RuleLabelLine>
          {field?.label ?? rule.rule.field}
          {negatable && rule.disabled && negated ? <NotMarker>Not</NotMarker> : null}
          {negatable && !rule.disabled ? (
            <NotToggle
              type="button"
              aria-pressed={negated}
              aria-label={`Not ${negatable.label}`}
              title={otherSideTaken ? `${otherSideLabel} already has a rule` : undefined}
              aria-describedby={otherSideTaken ? notReasonId : undefined}
              disabled={otherSideTaken}
              onClick={() => editQuery(toggleRuleSide)}
            >
              Not
            </NotToggle>
          ) : null}
          {negatable && !rule.disabled && otherSideTaken ? (
            <VisuallyHidden id={notReasonId}>{otherSideLabel} already has a rule</VisuallyHidden>
          ) : null}
          <SupportBadge support={field?.backendSupport} />
        </RuleLabelLine>
        {field?.hint ? <small>{field.hint}</small> : null}
      </RuleLabel>
      <RuleEditors>
        <RuleInline>
          <OperatorSelector
            {...common}
            rule={rule.rule}
            options={rule.operators}
            value={rule.rule.operator}
            field={rule.rule.field}
            fieldData={rule.fieldData}
            handleOnChange={rule.onChangeOperator}
            title="Operator"
          />
        </RuleInline>
        <ValueEditor
          {...common}
          rule={rule.rule}
          field={rule.rule.field}
          fieldData={rule.fieldData}
          operator={rule.rule.operator}
          value={rule.rule.value}
          valueSource={rule.rule.valueSource ?? 'value'}
          type={rule.valueEditorType}
          inputType={rule.inputType}
          values={rule.values as unknown[]}
          handleOnChange={rule.onChangeValue}
          title="Value"
        />
        {!rule.disabled && errors.length > 0 ? (
          <FieldError role="alert">{errors.join(' · ')}</FieldError>
        ) : null}
        {!rule.disabled && droppedReason ? (
          <NotAppliedNote role="note">Not applied: {droppedReason}</NotAppliedNote>
        ) : null}
        {dependency ? <FieldHint>{dependency}</FieldHint> : null}
        {negatable && !rule.disabled && !otherSideTaken ? (
          <AddSideButton type="button" onClick={() => editQuery(addOppositeRule)}>
            Add {otherSideLabel}
          </AddSideButton>
        ) : null}
      </RuleEditors>
      <RemoveRule
        {...common}
        ruleOrGroup={rule.rule}
        handleOnClick={rule.removeRule}
        label="×"
        title={label}
      />
    </RuleRowBox>
  );
};

/**
 * Operators as a segmented control.
 *
 * Hidden when the field has only one operator, and for negatable fields, whose row carries the Not switch.
 */
export const AudienceOperatorSelector = (props: OperatorSelectorProps) => {
  const field = audienceField(props.rule.field);
  if (!field || field.operators.length < 2 || isNegatableField(field)) return null;
  return (
    <SegmentedControl
      label={`${field.label} operator`}
      value={props.value as AudienceOperator}
      disabled={props.disabled}
      options={field.operators.map(operator => ({
        label: OPERATOR_LABELS[operator],
        value: operator
      }))}
      onChange={props.handleOnChange}
    />
  );
};

export const AudienceRemoveRule = (props: ActionProps) =>
  props.disabled ? null : (
    <RemoveRuleButton
      type="button"
      aria-label={`Remove ${props.title ?? 'rule'}`}
      title="Remove rule"
      onClick={event => props.handleOnClick(event)}
    >
      <CloseRounded />
    </RemoveRuleButton>
  );

const reasonsOf = (validation: boolean | ValidationResult | undefined): string[] =>
  validation && typeof validation === 'object' && !validation.valid
    ? (validation.reasons ?? []).map(String)
    : [];

const GENDER_OPTIONS = [
  { label: 'Any', value: ANY_GENDER },
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' }
];

/* Stored as fractions 0 to 1, edited as whole percents */
const toPercent = (fraction: unknown): number | null => {
  const value = toNumber(fraction);
  return value === undefined ? null : Math.round(value * 100);
};
const toFraction = (percent: number | null): number | '' => (percent == null ? '' : percent / 100);

const emptyToBlank = (value: number | null): number | '' => value ?? '';

const sideTone = (operator: string): 'include' | 'exclude' =>
  operator === NEGATED_OPERATOR ? 'exclude' : 'include';

/* The other side's values, so a state or brand id cannot be both included and excluded */
const otherSideValues = (props: ValueEditorProps): string[] => {
  const otherOperator = oppositeOperator(props.operator);
  const rule = props.schema
    .getQuery()
    ?.rules.find(
      item =>
        typeof item !== 'string' &&
        'field' in item &&
        item.field === props.field &&
        item.operator === otherOperator
    ) as RuleType | undefined;
  return asList(rule?.value);
};

const asList = (value: unknown): string[] => (Array.isArray(value) ? value.map(String) : []);
